import { BottomSheet } from "@/design-system/BottomSheet/BottomSheet";
import { Levels } from "@/design-system/Evolution/Levels";
import { EvoDetailPanel } from "@/features/Evolutions/EvoDetailPanel";
import { EvoFacts } from "@/features/Evolutions/EvoFacts";
import { useUpgradeFormatter } from "@/features/Evolutions/useUpgradeFormatter";
import { evolutionCategoriesByIdSelector } from "@/redux/evolutions/evolutionsSlice";
import { evolutionPathPair } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { EvoChainFromIds, useEvolutionsById } from "./EvoChainFromIds";

export interface EvoChainSheetProps {
  evolutionIds: number[];
  /** Option choices per step; missing or short means path 1 everywhere. */
  pathwayPath?: number[][];
  isOpen: boolean;
  onClose: () => void;
  /** Which step opens selected. */
  initialIndex?: number;
}

/**
 * A chain, step by step: the chain as selectable chips on top, then the
 * selected step laid out like its overview page — facts, upgrades and
 * requirements, levels. Read-only: the levels show the path the chain took,
 * they don't change it.
 */
export const EvoChainSheet = ({
  evolutionIds,
  pathwayPath,
  isOpen,
  onClose,
  initialIndex = 0,
}: EvoChainSheetProps) => {
  const { t } = useTranslation("evolutions");
  const { t: tCommon } = useTranslation("common");
  const resolve = useEvolutionsById();
  const categoriesById = useSelector(evolutionCategoriesByIdSelector);
  const formatUpgrade = useUpgradeFormatter();
  const [selected, setSelected] = useState(initialIndex);

  // Reopening on another step should show that step, not the last one viewed.
  useEffect(() => {
    if (isOpen) setSelected(initialIndex);
  }, [isOpen, initialIndex]);

  const pair = useMemo(
    () => evolutionPathPair(evolutionIds, pathwayPath),
    [evolutionIds, pathwayPath],
  );
  const evolution = resolve(evolutionIds[selected]);
  const stepPathway = pair.pathwayPath[selected];

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      // The page background, not the sheet's default surface: the sections
      // inside are surface cards and need the darker ground to read as cards.
      className="h-[90vh] bg-background touch-device:h-[85vh]"
      header={{ title: t("chain.detailedInfo"), closeLabel: tCommon("close") }}
    >
      <div className="mx-auto flex w-full max-w-content flex-col gap-8 px-4 pb-8 large:px-16 touch-device:px-4">
        <div className="flex flex-col gap-2">
          <span className="text-xs tracking-wider text-foreground-muted uppercase">
            {t("chain.stepOf", {
              step: selected + 1,
              total: evolutionIds.length,
            })}
          </span>
          <EvoChainFromIds
            evolutionIds={evolutionIds}
            pathwayPath={pair.pathwayPath}
            variant="selectable"
            connectors={false}
            selectedIndex={selected}
            onOpenStep={setSelected}
            className="gap-x-2"
          />
        </div>

        {evolution && (
          <>
            <EvoFacts
              evolution={evolution}
              category={categoriesById[evolution.categoryId]}
            />
            <EvoDetailPanel
              evolution={evolution}
              category={categoriesById[evolution.categoryId]}
              pathwayPath={stepPathway}
            />
            <Levels
              evolution={evolution}
              pathwayPath={stepPathway}
              formatUpgrade={formatUpgrade}
            />
          </>
        )}
      </div>
    </BottomSheet>
  );
};
